import * as React from 'react';
import { Routes, Route } from 'react-router-dom';
import { AdminPermissionWrappedProvider, Error404 } from 'ui-lib';

import { ManageAccountsComponent } from './list';
import { AddAccountComponent, EditAccountComponent } from './save';
import { useAccountStore, useAuthStore, useRoutesStore, useStores } from '../../../../store';

import accountCss from './account.css';
import { ITreeSelectDataSource } from '../../../../store/account-store';
import { AccountType, IAccount } from '../../../../dto/access-management/account-dto';
import { ExternalRepoSettings } from './external-repo-settings';
import { ADMIN_PERMISSIONS } from 'contexts/permissions/permission-provider';

export enum ACCOUNT_ROUTE_CODES {
    ROOT = 'ADMIN_ACCOUNTS',
    ADD = 'ADMIN_ACCOUNTS_ADD',
    EDIT = 'ADMIN_ACCOUNTS_EDIT',
    EXTERNAL_REPOSITORY_SETTINGS = 'ADMIN_ACCOUNT_EXTERNAL_REPOSITORY_SETTINGS',
}

interface ISelectableDataSource {
    viewableTypes?: AccountType[];
    selectableTypes: AccountType[];
    defaultAccountHierarchy?: IAccount;
}
export const useAccountTreeSelectDataSource = (options: ISelectableDataSource, otherDeps: any[] = []): ITreeSelectDataSource => {
    const { currentUser } = useAuthStore();
    const accountStore = useAccountStore();
    const dataSource = React.useMemo<ITreeSelectDataSource>(
        () => accountStore.getSelectableTreeDataSource(options.viewableTypes || Object.values(AccountType), options.selectableTypes, options.defaultAccountHierarchy),
        [currentUser, accountStore.accountHierarchy, ...otherDeps],
    );
    return dataSource;
};

export function ManageAccountPages() {
    const routeStore = useRoutesStore();
    const { permissionStore } = useStores();

    const rootRoute = routeStore.getRoute(ACCOUNT_ROUTE_CODES.ROOT);
    const addRoute = routeStore.getRoute(ACCOUNT_ROUTE_CODES.ADD);
    const editRoute = routeStore.getRoute(ACCOUNT_ROUTE_CODES.EDIT);
    const externalRepoRoute = routeStore.getRoute(ACCOUNT_ROUTE_CODES.EXTERNAL_REPOSITORY_SETTINGS);

    return (
        <Routes>
            <Route
                path='/'
                element={
                    <AdminPermissionWrappedProvider
                        permissionCodes={permissionStore.permissionCodes}
                        routes={routeStore.routes}
                        title={`Manage ${rootRoute.label}`}
                        requiredPermissionCodes={[ADMIN_PERMISSIONS.ACCOUNTS]}
                    >
                        <ManageAccountsComponent />
                    </AdminPermissionWrappedProvider>
                }
            />
            <Route
                path={addRoute.trimRootPath(rootRoute.path)}
                element={
                    <AdminPermissionWrappedProvider
                        permissionCodes={permissionStore.permissionCodes}
                        routes={routeStore.routes}
                        title={addRoute.label}
                        className={accountCss.save}
                        requiredPermissionCodes={[ADMIN_PERMISSIONS.ACCOUNTS_MODIFY]}
                    >
                        <AddAccountComponent />
                    </AdminPermissionWrappedProvider>
                }
            />
            <Route
                path={editRoute.trimRootPath(rootRoute.path)}
                element={
                    <AdminPermissionWrappedProvider
                        permissionCodes={permissionStore.permissionCodes}
                        routes={routeStore.routes}
                        title={editRoute.label}
                        className={accountCss.save}
                        requiredPermissionCodes={[ADMIN_PERMISSIONS.ACCOUNTS_MODIFY]}
                    >
                        <EditAccountComponent />
                    </AdminPermissionWrappedProvider>
                }
            />
            <Route
                path={externalRepoRoute.trimRootPath(rootRoute.path)}
                element={
                    <AdminPermissionWrappedProvider
                        permissionCodes={permissionStore.permissionCodes}
                        routes={routeStore.routes}
                        title='External Repository Settings'
                        className={accountCss.save}
                    >
                        <ExternalRepoSettings />
                    </AdminPermissionWrappedProvider>
                }
            />
            <Route path='*' element={<Error404 message='Page Not Found' />} />
        </Routes>
    );
}
